import { useEffect, useRef, useState } from "react";
import RoomBookingTable from "./Components/RoomBookingTable";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GET_RESERVATION_LIST_EXPORT_REQUEST } from "../../../../Utils/Constant";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";
import { hasAddAccess } from "../../../../Common/CheckAccess";

const ReservationList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const csvLinkRef = useRef(); // ref to CSVLink

  const [dataForExport, setDataForExport] = useState([]);
  const [seperateHeaders, setSeperateHeaders] = useState([]);
  const [exportFormat, setExportFormat] = useState("csv");
  const [permissions, setPermissions] = useState([]);

  const existingPermissions = localStorage.getItem("checkPermission");

  useEffect(() => {
    if (existingPermissions != undefined) {
      const parsedData = JSON.parse(existingPermissions);
      setPermissions(parsedData);
    }
  }, [existingPermissions]);

  const { reservationExport } = useSelector(
    (state) => state.roomReservationList
  );

  const parseCSV = (csv) => {
    const lines = csv?.split("\n");
    const rawHeaders = lines[0]?.split(",").map((h) => h.trim());

    const separateHeaders = rawHeaders.map((header) => ({
      label: header.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      key: header,
    }));
    setSeperateHeaders(separateHeaders);

    const headers = lines[0].split(",");

    const result = lines.slice(1).map((line) => {
      const values = line.split(",");
      const obj = {};
      headers.forEach((header, i) => {
        obj[header.trim()] = values[i]?.trim() || "";
      });
      return obj;
    });

    return result;
  };

  const downloadExcel = (data) => {
    try {
      // Ensure we have valid data
      if (!data || data.length === 0) {
        console.error("No data to export");
        return;
      }

      // Get headers directly from the first data object
      const headers = Object.keys(data[0]);

      // Create header labels (convert snake_case to Title Case)
      const headerLabels = headers.map((header) =>
        header.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      );

      // Create a new workbook
      const wb = XLSX.utils.book_new();

      // Convert data to worksheet format
      const wsData = [
        headerLabels, // Header row with formatted labels
        ...data.map((row) => headers.map((header) => row[header] || "")), // Data rows
      ];

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Set column widths for better formatting
      const colWidths = headers.map(() => ({ wch: 20 })); // Increased width
      ws["!cols"] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Room Bookings");

      // Generate Excel file and trigger download
      XLSX.writeFile(wb, "Room_Booking_List.xlsx");
    } catch (error) {
      console.error("Error generating Excel file:", error);
    }
  };

  useEffect(() => {
    if (
      typeof reservationExport === "string" &&
      reservationExport.trim() !== ""
    ) {
      const parsedData = parseCSV(reservationExport);
      setDataForExport(parsedData);

      // Trigger download after data is ready
      setTimeout(() => {
        if (exportFormat === "csv") {
          // Use existing CSV download
          csvLinkRef.current?.link?.click();
        } else if (exportFormat === "excel") {
          // Use simplified Excel download with parsed data directly
          downloadExcel(parsedData);
        }

        // Reset state
        setDataForExport([]);
        setSeperateHeaders([]);
        dispatch({ type: "RESET_RESERVATION_EXPORT" });
      }, 100);
    }
  }, [reservationExport, exportFormat, dispatch]);

  const handleReservationExport = () => {
    dispatch({
      type: GET_RESERVATION_LIST_EXPORT_REQUEST,
      payload: { format: "csv" }, // Always send CSV format to API
    });
  };

  const handleNavigate = () => {
    navigate("/reservation/add-room-booking");
  };

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="row g-3">
        <div className="col-sm-12">
          <div className="d-flex align-items-center justify-content-between flex-wrap">
            <h3 className="fw-bold mb-0">Room Booking List</h3>
            <div className="col-auto d-flex w-sm-100 gap-4">
              <div className="col-auto d-flex w-sm-100 gap-2 align-items-center">
                {/* Format Selection Dropdown */}
                <select
                  className="form-select"
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                  style={{ width: "auto", minWidth: "100px" }}
                >
                  <option value="csv">CSV</option>
                  <option value="excel">Excel</option>
                </select>

                <button
                  type="button"
                  className="btn btn-dark"
                  onClick={handleReservationExport}
                >
                  Export
                </button>

                {/* Hidden CSVLink for auto-download (only used for CSV) */}
                <CSVLink
                  data={dataForExport}
                  headers={seperateHeaders}
                  filename="Room_Booking_List.csv"
                  className="d-none"
                  ref={csvLinkRef}
                  target="_blank"
                >
                  Download
                </CSVLink>
              </div>
              {hasAddAccess(permissions, "reservation") && (
                <button
                  type="button"
                  className="btn btn-dark"
                  onClick={handleNavigate}
                >
                  Room Book
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <RoomBookingTable />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationList;
